import { useEffect, useState } from "react";
import {
  SmartAlert,
  SmartLoaderInterface,
  SmartSoftButton,
  SmartTable,
  SmartTableNewInterface,
} from "sds_react_toolkit";
import { INDEX_COMPANY_URLS, COMPANY_URLS } from "../../api/ManageUrls";
import { useSiteContext } from "../../contexts/SiteProvider";
import { showAlertAutoClose } from "../../services/notifyService";
import { get, post } from "../../services/smartApiService";
import IndexCompanyForm from "./IndexCompanyForm";

const IndexCompanyTable = () => {
  const [tabData, setTabData] = useState<any[]>([]);
  const [selectedIndexId, setSelectedIndexId] = useState<string>("all");
  const [indices, setIndices] = useState<any[]>([]);

  const { openModal, setTitle } = useSiteContext();

  useEffect(() => {
    setTitle("Index Composition Management");
    // Fetch all indices for filter
    get(COMPANY_URLS.GET_INDICES).subscribe({
      next: (response: any) => {
        setIndices(response.data || []);
      },
      error: (err) => {
        console.error("Failed to fetch indices:", err);
      },
    });
  }, []);

  useEffect(() => {
    loadTableData();
  }, [selectedIndexId]);

  const loadTableData = () => {
    const URL =
      selectedIndexId === "all"
        ? INDEX_COMPANY_URLS.GET_ALL
        : `${INDEX_COMPANY_URLS.GET_BY_INDEX}${selectedIndexId}`;

    const subscription = get(URL).subscribe((response) => {
      setTabData(response.data || []);
    });
    return () => subscription.unsubscribe();
  };

  const deleteData = (id: number) => {
    const subscription = post(`${INDEX_COMPANY_URLS.DELETE}${id}`, {}).subscribe(() => {
      showAlertAutoClose("Company removed from index successfully", "success");
      loadTableData();
    });
    return () => subscription.unsubscribe();
  };

  const deleteAllData = (indexId: string | number) => {
    const subscription = post(`${INDEX_COMPANY_URLS.DELETE_ALL}${indexId}`, {}).subscribe(() => {
      showAlertAutoClose("All companies removed from index", "success");
      loadTableData();
    });
    return () => subscription.unsubscribe();
  };

  const openDeleteModal = (row: any) => {
    const alertProps: SmartLoaderInterface.SmartAlertInterface = {
      title: (
        <span className="has-text-danger">
          <i className="fa fa-exclamation-triangle"></i> Confirm Removal
        </span>
      ),
      alertFunction: (option: any) => {
        if (option === "yes") {
          deleteData(row.id);
          SmartAlert.hide();
        }
      },
      content: (
        <p>
          Do you want to remove <strong>{row.company_name}</strong> from{" "}
          <strong>{row.index_name}</strong>? <br />
          <strong>This action cannot be undone.</strong>
        </p>
      ),
      className: "custom-alert",
    };
    SmartAlert.show(alertProps);
  };

  const openDeleteAllModal = () => {
    if (selectedIndexId === "all") {
      showAlertAutoClose("Please select a specific index first", "warning");
      return;
    }
    const indexName = indices.find((i: any) => String(i.id) === String(selectedIndexId))?.name || selectedIndexId;
    const alertProps: SmartLoaderInterface.SmartAlertInterface = {
      title: (
        <span className="has-text-danger">
          <i className="fa fa-exclamation-triangle"></i> Delete All Companies
        </span>
      ),
      alertFunction: (option: any) => {
        if (option === "yes") {
          deleteAllData(selectedIndexId);
          SmartAlert.hide();
        }
      },
      content: (
        <p>
          Remove <strong>ALL companies</strong> from <strong>{indexName}</strong>?<br />
          This will delete all {tabData.length} entries in one go.<br />
          <strong>This action cannot be undone.</strong>
        </p>
      ),
      className: "custom-alert",
    };
    SmartAlert.show(alertProps);
  };

  const openIndexCompanyForm = () => {
    const options = {
      title: "Add Company to Index",
      content: (
        <IndexCompanyForm
          loadTableData={loadTableData}
          selectedIndexId={selectedIndexId !== "all" ? Number(selectedIndexId) : undefined}
        />
      ),
      className: "sd-efl-modal",
      closeBody: false,
      width: 80,
    };
    openModal(options);
  };

  const buttons = [
    {
      label: "",
      type: "icon",
      leftIcon: "fa fa-trash",
      classList: ["smart-hims-table-delete-icon"],
      onClick: (data: any) => openDeleteModal(data),
    },
  ];

 const columns: SmartTableNewInterface.SmartTableNewColumnConfig[] = [
  { title: "S.NO", index: "s_no", type: "sno", width: "5" },

  {
    title: "Index",
    index: "index_name",
    width: "20",
    isSortable: true,
    isFilterable: true,

    valueFunction: (row: any) => (
      <div>
        <strong>{row.index_name}</strong>
        <br />
        <small className="has-text-grey">{row.index_symbol}</small>
      </div>
    ),
  },

  {
    title: "Company",
    index: "company_name",
    width: "20",
    isSortable: true,
    isFilterable: true,
 
    valueFunction: (row: any) => (
      <div>
        <strong>{row.company_name}</strong>
        <br />
        <small className="has-text-grey">{row.company_symbol}</small>
      </div>
    ),
  },

  {
    title: "Weightage",
    index: "weightage",
    width: "10",
    isSortable: true,
  
    valueFunction: (row: any) =>
      row.weightage !== null && row.weightage !== undefined
        ? `${Number(row.weightage).toFixed(2)}%`
        : "-",
  },

  {
    title: "Action",
    index: "action",
    type: "buttons",
    buttons,
    width: "10",
  },
];


  const tableTop: SmartTableNewInterface.SmartTableNewTopProps[] = [
    {
      type: "CUSTOM",
      widthClass: "is-4",
      custom: <p className="is-size-4 has-text-weight-bold">Index Composition</p>,
    },
    {
      type: "CUSTOM",
      widthClass: "is-3",
      custom: (
        <div className="field">
          <div className="control">
            <div className="select is-fullwidth">
              <select
                value={selectedIndexId}
                onChange={(e) => setSelectedIndexId(e.target.value)}
              >
                <option value="all">All Indices</option>
                {indices.map((index) => (
                  <option key={index.id} value={index.id}>
                    {index.name} ({index.symbol})
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      ),
    },
    {
      type: "SEARCH",
      widthClass: "is-3",
      align: "JUSTIFY",
    },
    {
      type: "BUTTONS",
      widthClass: "is-1",
      align: "RIGHT",
      buttons: [
        {
          type: "REFRESH",
          action: loadTableData,
        },
      ],
    },
    {
      type: "CUSTOM",
      widthClass: "is-2",
      align: "RIGHT",
      custom: (
        <div style={{ display: "flex", gap: "6px" }}>
          {selectedIndexId !== "all" && (
            <SmartSoftButton
              label="Delete All"
              onClick={() => openDeleteAllModal()}
              leftIcon="fa-trash"
              classList={["is-danger", "is-light", "is-small"]}
            />
          )}
          <SmartSoftButton
            label="Add"
            onClick={() => openIndexCompanyForm()}
            leftIcon="fa-plus"
            classList={["smart-action-button"]}
          />
        </div>
      ),
    },
  ];

  return (
    <div className="sd-efl-input">
      <SmartTable
        columns={columns}
        data={tabData}
        tableTop={tableTop}
        tableProps={{
          className: "is-hoverable is-striped dsc-table-container",
          isResponsive: true,
          searchPlaceHolder: "Search index composition",
        }}
        paginationProps={{
          pageSize: 10,
        }}
      />
    </div>
  );
};

export default IndexCompanyTable;
